import { Component, Inject, OnInit, OnDestroy, PLATFORM_ID, Renderer2, ChangeDetectorRef, ViewChild, ComponentRef, AfterViewInit, Injector, signal, DOCUMENT } from '@angular/core';
import { Router, NavigationStart, RouterOutlet } from '@angular/router';
import { AuthenticationService } from './_services';
import { AsyncPipe, isPlatformBrowser, isPlatformServer, Location, NgOptimizedImage } from '@angular/common';
import { Angulartics2Facebook, Angulartics2Mixpanel } from 'angulartics2';
import { Angulartics2GoogleGlobalSiteTag } from 'angulartics2';
import { environment } from 'src/environments/environment';
import { AnalyticsService } from './_services/analytics.service';
import { ConsentService } from './_services/consent.service';
import { LanguageService } from './_services/language.service';
import { BreakpointObserver, MediaMatcher } from '@angular/cdk/layout';
import { MaterialContent } from './components/page/material-content.component';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { CdkPortalOutletAttachedRef, PortalModule } from '@angular/cdk/portal';
import { SidenavService } from 'src/app/_services/sidenav.service';
import { BotDetectionService } from './_services/bot-detection.service';
import { ScrollService } from './_services/scroll.service';
import { IS_MOBILE_AGENT } from './tokens';
import { NavigationComponent } from './components/navigation/navigation.component';
import { FooterComponent } from './components/footer/footer.component';
import { InstantBottomSheet } from './components/instant-bottom-sheet/instant-bottom-sheet.component';
import { InstantConsent } from './components/instant-consent/instant-consent.component';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';

interface ExtendedMixpanel extends Mixpanel {
  __SV?: number;
  _i?: any[];
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterOutlet, 
    NgOptimizedImage,
    AsyncPipe,
    NavigationComponent,
    FooterComponent,
    InstantBottomSheet,
    InstantConsent,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    PortalModule
  ],
})
export class AppComponent extends MaterialContent implements OnInit, OnDestroy, AfterViewInit {
  
  title = 'Listx';
  isWebView: boolean = false;
  
  phoneQuery: MediaQueryList | undefined;
  
  private phoneQueryListener: () => void;
  private isMobileAgent: boolean | undefined;
  
  showConsent = signal<boolean>(false);

  @ViewChild(MatSidenav) sidenav: MatSidenav;
    
  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private angularticsMixpanel: Angulartics2Mixpanel,
    private angularticsGTag: Angulartics2GoogleGlobalSiteTag,
    private angularticsFacebook: Angulartics2Facebook,
    private consentService: ConsentService,
    private location: Location,
    private analyticsService: AnalyticsService,
    private changeDetectorRef: ChangeDetectorRef, 
    breakpointObserver: BreakpointObserver,
    public sidenavService: SidenavService,
    private botDetectionService: BotDetectionService,
    media: MediaMatcher,
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) private document: any,
    private renderer: Renderer2,
    private scrollService: ScrollService,
    injector: Injector
  ) {
    
    super(breakpointObserver, true)
    
    this.showConsent.set(this.needToShowConsent());

    try {
      this.isMobileAgent = injector.get(IS_MOBILE_AGENT);  
      console.log("Mobile agent detected: " + this.isMobileAgent);
    } catch (e) {
      console.log("Didn't determine whether agent is mobile or not");
    }
    
    if (isPlatformBrowser(this.platformId)) {
      
      this.phoneQuery = media.matchMedia('(max-width: 1007px)');
      this.phoneQueryListener = () => this.onPhoneQueryChange();
      this.phoneQuery.addListener(this.phoneQueryListener);
      
      this.router.events.subscribe(event => {
        
        if (event instanceof NavigationStart) {
          this.scrollService.savePosition(this.router.url, window.scrollY);
          window.scrollTo(0, 0); // Scroll to top before the old page is destroyed
        }
      });
    }
  }
  
  ngOnInit(): void {
    
    if (isPlatformBrowser(this.platformId)) {
      this.authenticationService.tryLogIn();  
      this.injectMixpanel();
      this.injectGTag();
      this.injectFacebookPixel();
      this.analyticsService.initAnalytics();
      
      if (environment.mixpanelEnabled) {
        this.angularticsMixpanel.startTracking();
      }

      this.angularticsGTag.startTracking();  
      this.angularticsFacebook.startTracking();
      this.injectTidio();
    } else {
      this.replaceAlternateLinks();
    }
  }
  
  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.body.removeAttribute('angular-loading')
      this.toggleSidenav(!this.phoneQuery!.matches);
    }
  }
  
  isMobile(): boolean {
    
    if (isPlatformServer(this.platformId)) {
      return this.isMobileAgent!!;
    }
    
    return this.phoneQuery!! && this.phoneQuery.matches;
  }
  
  private onPhoneQueryChange(): void {
    
    this.changeDetectorRef.detectChanges();
    this.toggleSidenav(!this.phoneQuery?.matches);
  }

  private toggleSidenav(open: boolean) {
    
    console.log("Toggle sidenav open=" + open);
    
    if (open) {
      this.sidenav.open();
    } else {
      this.sidenav.close();
    }
  }
    
  private needToShowConsent(): boolean {
    return !this.botDetectionService.isAnyBot() &&
        this.consentService.getConsentState() === null
  }
  
  ngOnDestroy(): void {
    if (this.phoneQuery) {
      this.phoneQuery.removeListener(this.phoneQueryListener);
    }
  }
  
  onPageSpecificNavigationPortalsAttached(ref: CdkPortalOutletAttachedRef | null) {
    if (ref instanceof ComponentRef) {
      ref.instance.onNavigationCompleted.subscribe(() => {
        if (this.phoneQuery && this.phoneQuery.matches) {
          this.sidenav.close();
        }
      });
    }
  }
  
  private injectMixpanel() {
    
    if (!environment.mixpanelEnabled) {
      console.log("Mixpanel disabled");
      return;  
    }
    
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js';
    
    this.renderer.appendChild(this.document.body, script);
  }
  
  private injectGTag() {
    let script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js`;

    this.renderer.appendChild(this.document.body, script);
  }
  
  private injectFacebookPixel() {
    
    let script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://connect.facebook.net/en_US/fbevents.js`;

    this.renderer.appendChild(this.document.body, script);
  }
  
  private injectTidio() {
    let script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `//code.tidio.co/${environment.tidio.id}.js`;

    this.renderer.appendChild(this.document.body, script);
  }
  
  private replaceAlternateLinks() {
    this.removeAlternateLinks();
    this.addAlternateLinks();
  }
  
  private removeAlternateLinks() {
    const existingLinks = this.document.querySelectorAll('link[rel="alternate"]');
    existingLinks.forEach((link: any) => {
      this.renderer.removeChild(this.document.head, link);
    });
  }
  
  isAuthenticated(): boolean {
    return this.authenticationService.isAuthenticated();
  }
  
  closeConsentDialog() {
    this.showConsent.set(false);
    this.changeDetectorRef.detectChanges();
  }
  
  async navigate(path: string) {
    this.router.navigate([path]);
    if (this.phoneQuery && this.phoneQuery.matches) {
      this.sidenav.close();
    }
  }
  
  private addAlternateLinks() {
    console.log(this.location.path())
    LanguageService.SUPPORTED_LOCALES.forEach(locale => {
      
      const localeSpecificURL = `${environment.baseURL}/${locale}${this.location.path()}`;
      this.addAlternateLink(locale, localeSpecificURL);
      const localeParts = locale.split("-");
      if (localeParts.length == 2) {
        this.addAlternateLink(localeParts[0], localeSpecificURL);
      }
    })
    
    this.addAlternateLink('x-default', `${environment.baseURL}${this.location.path()}`);
  }
  
  private addAlternateLink(language: string, url: string) {
    const link = this.renderer.createElement('link');
    this.renderer.setAttribute(link, 'rel', 'alternate');
    this.renderer.setAttribute(link, 'hreflang', language);
    this.renderer.setAttribute(link, 'href', url);
    this.renderer.appendChild(this.document.head, link);
  }
}
